/**
 * Rate limit for repeating log lines: at most one line per key and window.
 * Held-back repeats are summarized on the next line that gets through.
 */
export class LogThrottle {
  private readonly entries = new Map<string, { loggedAt: number; held: number }>();

  constructor(
    private readonly windowMs = 60_000,
    private readonly maxKeys = 200
  ) {}

  /**
   * Counts one occurrence of `key`. Returns `null` when it should not be
   * logged, otherwise a suffix for the log line: empty for a first
   * occurrence, ` (+N similar since HH:MM:SS)` after held-back repeats.
   */
  hit(key: string, now = Date.now()): string | null {
    const entry = this.entries.get(key);
    if (entry && now - entry.loggedAt < this.windowMs) {
      entry.held += 1;
      return null;
    }
    const suffix = entry?.held
      ? ` (+${entry.held} similar since ${new Date(entry.loggedAt).toLocaleTimeString()})`
      : '';
    // Re-inserting keeps the map in recency order, so the oldest key is the
    // first one to go once the cap is reached.
    this.entries.delete(key);
    this.entries.set(key, { loggedAt: now, held: 0 });
    if (this.entries.size > this.maxKeys) {
      this.entries.delete(this.entries.keys().next().value!);
    }
    return suffix;
  }
}
